import type { FeatureGridBlockPayload, FeatureGridGroup } from '@opinix-cloud/shared';

interface FeaturesProps {
  content: FeatureGridBlockPayload;
}

/**
 * Big "what's inside" feature strip with alternating left/right rows.
 * Each `group` in the payload becomes one row; `iconKey` selects
 * which of the four bundled visuals to render alongside the text.
 *
 * Visuals stay in code (CommsVisual, DashboardVisual, …) — the CMS
 * never touches JSX. Authors edit the eyebrow + heading + blurb +
 * bullet list per row.
 */
export function Features({ content }: FeaturesProps): JSX.Element {
  const groups = content.groups ?? [];
  return (
    <section className="bg-steel-50 py-24 sm:py-32">
      <div className="container-x">
        <div className="max-w-2xl">
          {content.eyebrow && <p className="eyebrow">{content.eyebrow}</p>}
          {content.heading && (
            <h2 className="mt-3 section-title">{content.heading}</h2>
          )}
        </div>
        <div className="mt-16 space-y-20 sm:space-y-28">
          {groups.map((g, idx) => (
            <FeatureRow
              key={g.heading ?? g.eyebrow ?? idx}
              group={g}
              reverse={idx % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureRow({
  group,
  reverse,
}: {
  group: FeatureGridGroup;
  reverse: boolean;
}): JSX.Element {
  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
        reverse ? 'lg:[&>:first-child]:order-2' : ''
      }`}
    >
      <div>
        {group.eyebrow && <p className="eyebrow">{group.eyebrow}</p>}
        {group.heading && (
          <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-navy-900 leading-tight">
            {group.heading}
          </h3>
        )}
        {group.blurb && (
          <p className="mt-4 text-lg text-steel-600 leading-relaxed">
            {group.blurb}
          </p>
        )}
        {group.items.length > 0 && (
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {group.items.map((b) => (
              <li
                key={b.title}
                className="flex items-start gap-2 text-sm text-steel-700"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 20 20"
                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-cyan-500"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.4 7.4a1 1 0 0 1-1.4 0L3.3 9.5a1 1 0 1 1 1.4-1.4l3.9 3.9 6.7-6.7a1 1 0 0 1 1.4 0Z"
                  />
                </svg>
                {b.title}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="relative">
        <FeatureVisual iconKey={group.iconKey} />
      </div>
    </div>
  );
}

function FeatureVisual({ iconKey }: { iconKey: string | null | undefined }): JSX.Element {
  switch (iconKey) {
    case 'comms':
      return <CommsVisual />;
    case 'dashboard':
      return <DashboardVisual />;
    case 'library':
      return <LibraryVisual />;
    case 'aar':
    default:
      return <AARVisual />;
  }
}

/* ---------- Visuals: pure-SVG illustrations, no images ---------- */

function VisualFrame({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="relative rounded-xl bg-navy-900 p-5 shadow-2xl ring-1 ring-white/10 overflow-hidden">
      <div className="absolute inset-0 bg-radial-cyan opacity-60 pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-1.5 mb-4">
          <span className="h-2.5 w-2.5 rounded-full bg-ember-500/60" />
          <span className="h-2.5 w-2.5 rounded-full bg-cyan-500/60" />
          <span className="h-2.5 w-2.5 rounded-full bg-steel-500/60" />
        </div>
        {children}
      </div>
    </div>
  );
}

function CommsVisual(): JSX.Element {
  return (
    <VisualFrame>
      <div className="space-y-2">
        {[
          { ch: 'TAC-1', who: 'Unit 12', text: 'Subject moving north on Main', t: '14:02:11', live: true },
          { ch: 'TAC-1', who: 'Dispatch', text: 'Copy. Air-1 stand by.', t: '14:02:18' },
          { ch: 'TAC-2', who: 'Unit 4', text: 'On scene, securing perimeter.', t: '14:02:29' },
          { ch: 'TAC-1', who: 'Unit 12', text: 'Visual on subject. Approaching.', t: '14:02:41', live: true },
        ].map((m, i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-md bg-white/5 px-3 py-2 text-xs"
          >
            <span className="font-mono font-bold text-cyan-400 w-14">{m.ch}</span>
            <span className="font-semibold text-white w-20 truncate">{m.who}</span>
            <span className="text-steel-300 flex-1 truncate">{m.text}</span>
            <span className="font-mono text-steel-400 text-[10px]">{m.t}</span>
            {m.live && (
              <span className="h-1.5 w-1.5 rounded-full bg-ember-500 animate-pulse" />
            )}
          </div>
        ))}
      </div>
    </VisualFrame>
  );
}

function DashboardVisual(): JSX.Element {
  return (
    <VisualFrame>
      <div className="grid grid-cols-3 gap-2">
        {[
          'CCTV-A',
          'CCTV-B',
          'Map',
          'Intel',
          'Status',
          'Roster',
        ].map((label, i) => (
          <div
            key={label}
            className="aspect-video rounded-md bg-gradient-to-br from-navy-700 to-navy-800 ring-1 ring-white/10 flex items-center justify-center relative overflow-hidden"
          >
            {i === 2 && (
              <svg viewBox="0 0 80 50" className="absolute inset-0 w-full h-full opacity-30">
                <path d="M0 30 Q20 15 40 25 T80 20" stroke="#11C0F3" strokeWidth="1" fill="none" />
                <path d="M0 40 Q20 25 40 35 T80 30" stroke="#11C0F3" strokeWidth="1" fill="none" />
                <circle cx="40" cy="25" r="2" fill="#f97316" />
              </svg>
            )}
            <span className="font-mono text-[10px] font-semibold text-cyan-400 relative">
              {label}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] font-mono text-steel-400">
        <span>6 nodes synchronized</span>
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-500 animate-pulse" />
          live
        </span>
      </div>
    </VisualFrame>
  );
}

function LibraryVisual(): JSX.Element {
  const items = [
    { name: 'Active Threat — Mall', tag: 'TEMPLATE', updates: '12 versions' },
    { name: 'Mass Casualty — Stadium', tag: 'CUSTOM', updates: '4 versions' },
    { name: 'School Lockdown — K-8', tag: 'TEMPLATE', updates: '8 versions' },
    { name: 'Severe Weather — EOC', tag: 'CUSTOM', updates: '2 versions' },
  ];
  return (
    <VisualFrame>
      <div className="space-y-2">
        {items.map((it) => (
          <div
            key={it.name}
            className="flex items-center justify-between rounded-md bg-white/5 px-3 py-2.5 text-xs"
          >
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-white truncate">
                {it.name}
              </div>
              <div className="font-mono text-[10px] text-steel-400 mt-0.5">
                {it.updates}
              </div>
            </div>
            <span
              className={`font-mono text-[9px] font-bold px-2 py-0.5 rounded ${
                it.tag === 'TEMPLATE'
                  ? 'bg-cyan-500/15 text-cyan-300'
                  : 'bg-ember-500/15 text-ember-500'
              }`}
            >
              {it.tag}
            </span>
          </div>
        ))}
      </div>
    </VisualFrame>
  );
}

function AARVisual(): JSX.Element {
  return (
    <VisualFrame>
      <div className="space-y-3">
        <div className="flex items-center justify-between text-[10px] font-mono text-steel-400">
          <span>14:00:00</span>
          <span>14:30:00</span>
          <span>15:00:00</span>
        </div>
        {[
          { label: 'TAC-1', segments: [25, 10, 35, 15, 15] },
          { label: 'TAC-2', segments: [15, 30, 10, 25, 20] },
          { label: 'Dispatch', segments: [40, 5, 20, 25, 10] },
          { label: 'CCTV', segments: [100, 0, 0, 0, 0] },
        ].map((track) => (
          <div key={track.label} className="flex items-center gap-3">
            <span className="font-mono text-[10px] font-bold text-cyan-400 w-14">
              {track.label}
            </span>
            <div className="flex-1 h-2 rounded bg-white/5 overflow-hidden flex">
              {track.segments.map((w, i) => (
                <div
                  key={i}
                  style={{ width: `${w}%` }}
                  className={
                    i % 2 === 0
                      ? 'bg-cyan-500/60'
                      : 'bg-ember-500/40'
                  }
                />
              ))}
            </div>
          </div>
        ))}
        <div className="pt-2 border-t border-white/10 flex items-center justify-between text-[10px] font-mono">
          <span className="text-steel-400">▶ 14:18:42</span>
          <span className="text-ember-500 font-bold">DECISION POINT</span>
        </div>
      </div>
    </VisualFrame>
  );
}
